import { useState, useMemo, useCallback, useEffect } from 'react';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import PageHeader from '../components/common/PageHeader';
import Chip from '../components/common/Chip';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import Icon from '../components/common/Icon';
import { IlloPortfolio } from '../components/common/Illustrations';
import { Select, DateField, inputSmClass, labelClass } from '../components/common/FormControls';
import { useUI } from '../context/UIContext';
import { useApp } from '../context/AppContext';
import { companiesAPI } from '../services/api';
import {
  computeCompanyDeadlines,
  normalizeEntityType,
  formatDateEN,
  ENTITY_LABELS,
  EVENT_BASED_FORMS,
  isRegistryFilingSource,
} from '../utils/agmDeadlineEngine';

// Status → soft Chip fill in the earthy palette (no borders, rounded-full).
const STATUS_CHIP = {
  overdue: { bg: '#f2e9e6', color: '#9a564a' },
  'due-soon': { bg: '#f3ebd7', color: '#8a6a1f' },
  'pending-agm': { bg: '#f2eee6', color: '#8a7d66' },
  compliant: { bg: '#e8efe5', color: '#4a6b3f' },
  // A recorded filing is fact, not a forecast — it gets its own chip so it can
  // never be confused with "on track" (a due date that simply hasn't passed).
  filed: { bg: '#e2ece6', color: '#2f6b4f' },
};

function statusLabel(status) {
  if (status === 'overdue') return 'Overdue';
  if (status === 'due-soon') return 'Due Soon';
  if (status === 'pending-agm') return 'Pending AGM';
  if (status === 'filed') return 'Filed';
  if (status === 'compliant') return 'On Track';
  return 'Pending';
}

// MCA SRN: one letter followed by 8 digits (e.g. T12345678). Used ONLY to hint.
// Never a gate — see the dialog copy: evidence is optional, and an odd or legacy
// SRN must not stand between a user and recording a filing that really happened.
const SRN_PATTERN = /^[A-Z]\d{8}$/;

/** Date → "YYYY-MM-DD" in LOCAL time (toISOString would shift the day east of UTC). */
function toIsoDay(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Evidence capture for "Mark filed".
 *
 * Why a dialog at all: the flag alone is an unverifiable assertion, and the one
 * thing that makes a recorded filing checkable later is the challan's SRN. But
 * the quick path has to stay quick — a professional clearing a year of backlog
 * will not stop to find eight challans, and if marking filed becomes a chore
 * they stop doing it and the whole penalty estimate goes wrong. So both routes
 * out are primary: "Save & mark filed" with whatever was typed, and
 * "Mark filed without details" which skips the fields entirely.
 *
 * Nothing here blocks on the SRN. A format mismatch shows a soft amber note and
 * still saves as typed.
 */
function FilingEvidenceDialog({ row, companyName, mode, saving, onCancel, onSave }) {
  const [srn, setSrn] = useState(row.filedSrn || '');
  const [filedOn, setFiledOn] = useState(toIsoDay(row.filedOn));

  useEffect(() => {
    const handler = (event) => { if (event.key === 'Escape' && !saving) onCancel(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel, saving]);

  const trimmedSrn = srn.trim().toUpperCase();
  const srnOdd = trimmedSrn !== '' && !SRN_PATTERN.test(trimmedSrn);
  // A filing date in the future is a typo, not a preference — caught here so the
  // user sees it next to the field instead of as a server error toast.
  const futureDate = !!filedOn && filedOn > toIsoDay(new Date());
  const editing = mode === 'edit';

  const submit = (withDetails) => {
    if (withDetails && futureDate) return;
    onSave({
      srn: withDetails ? trimmedSrn : '',
      filedOn: withDetails ? (filedOn || null) : null,
    });
  };

  return (
    // z-1090, deliberately BELOW the 1100 the DateField's portalled calendar
    // uses: at equal z-index the calendar only wins on DOM order, which is not a
    // contract. One notch down makes the picker reliably reach above the dialog.
    <div
      className="fixed inset-0 z-[1090] flex items-center justify-center p-4"
      style={{ background: 'rgba(8,17,31,0.55)' }}
      onClick={(event) => { if (event.target === event.currentTarget && !saving) onCancel(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={editing ? 'Filing details' : `Mark ${row.form} filed`}
        className="relative bg-white rounded-2xl w-[400px] max-w-[92vw] overflow-hidden"
        style={{ boxShadow: '0 12px 40px rgba(8,17,31,0.16), 0 2px 8px rgba(8,17,31,0.06)' }}
      >
        <div className="p-4">
          <div className="text-[17px] font-bold" style={{ color: '#08111f', fontFamily: "'Cormorant Garamond', serif" }}>
            {editing ? `Filing details — ${row.form}` : `Mark ${row.form} filed`}
          </div>
          <div className="text-[12px] text-[#7a7060] mt-[2px] mb-3">
            {[row.cycleFyLabel, companyName].filter(Boolean).join(' · ')}
          </div>

          <div className="mb-3">
            <label className={labelClass} htmlFor="filing-srn">SRN (optional)</label>
            <input
              id="filing-srn"
              className={inputSmClass}
              value={srn}
              disabled={saving}
              autoFocus
              placeholder="T12345678"
              // Uppercased as typed: SRNs are always uppercase, and normalising
              // here keeps the stored value and the hint check in agreement.
              onChange={(event) => setSrn(event.target.value.toUpperCase())}
            />
            {srnOdd && (
              <div className="mt-1 flex gap-1.5 text-[10.5px] leading-[1.35] text-[#8a6a1f]">
                <Icon name="info" size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>An MCA SRN is normally one letter and 8 digits. Saved as typed either way.</span>
              </div>
            )}
          </div>

          <div className="mb-3">
            <label className={labelClass}>Date filed (optional)</label>
            <DateField size="sm" value={filedOn} onChange={setFiledOn} placeholder="Date the form went in" disabled={saving} invalid={futureDate} />
            {futureDate && (
              <div className="mt-1 text-[10.5px] leading-[1.35] text-[#9a564a]">
                That date is in the future — a form cannot have been filed yet.
              </div>
            )}
          </div>

          <div className="rounded-lg px-3 py-2 mb-3 text-[11.5px] leading-[1.45] text-[#7a7060]" style={{ background: '#f8f5ef' }}>
            Evidence is optional. Recording the SRN just means this filing can be
            checked against MCA later{editing ? '' : ' — you can add it any time'}.
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="flex-1" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
            <Button variant="ink" size="sm" className="flex-1" onClick={() => submit(true)} loading={saving} disabled={futureDate}>
              {editing ? 'Save details' : 'Save & mark filed'}
            </Button>
          </div>
          {/* The escape hatch that keeps the quick path quick. In edit mode the
              same button means "drop the evidence I recorded earlier". */}
          <button
            type="button"
            disabled={saving}
            onClick={() => submit(false)}
            className="mt-2 w-full text-[11.5px] underline text-[#7a7060] disabled:opacity-50"
          >
            {editing
              ? (row.filedHasEvidence ? 'Remove details, keep it marked filed' : 'Keep it marked filed without details')
              : 'Mark filed without details'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AnnualFilingPage() {
  const { showToast } = useUI();
  const { companies, activeCompany, openAddCompanyModal, fetchCompanies } = useApp();
  const [cin, setCin] = useState('');
  const [selectedCin, setSelectedCin] = useState(activeCompany?.cin || '');
  const [savingRowKey, setSavingRowKey] = useState(null);
  const [savingXbrl, setSavingXbrl] = useState(false);
  const [fetchingMca, setFetchingMca] = useState(false);
  // Evidence capture: { row, mode: 'mark' | 'edit' } while the dialog is open.
  const [evidenceFor, setEvidenceFor] = useState(null);
  // Un-ticking "has filed in XBRL before" is a correction, not a routine toggle,
  // so it goes through a confirm that says what the rule actually binds.
  const [confirmStickyOff, setConfirmStickyOff] = useState(false);

  // Selected company for the checklist
  const selectedCompany = useMemo(() => {
    if (!selectedCin) return activeCompany || companies[0] || null;
    return companies.find((c) => String(c.cin || '').toUpperCase() === selectedCin.toUpperCase()) || null;
  }, [selectedCin, companies, activeCompany]);

  // Read-only collaborators can see filed state but not change it — the
  // filings PATCH requires read_write, so don't render a control that 403s.
  // Missing metadata (older cached list) errs on showing the control; the
  // server remains the enforcement point.
  const canMarkFiled = selectedCompany
    ? selectedCompany.myAccessLevel == null || ['read_write', 'admin'].includes(selectedCompany.myAccessLevel)
    : false;

  // Compute deadlines for the selected company
  const companyResult = useMemo(() => {
    if (!selectedCompany) return null;
    return computeCompanyDeadlines(selectedCompany);
  }, [selectedCompany]);

  // Build checklist rows from engine results
  const checklist = useMemo(() => {
    if (!companyResult) return [];
    const rows = [];

    // AGM deadline first (if applicable). Not markable here — "AGM held" is
    // driven by the company's Last AGM date, not by a filing record.
    if (companyResult.agmDeadline) {
      rows.push({
        id: 'agm',
        formKey: 'agm',
        form: companyResult.agmDeadline.code,
        purpose: companyResult.agmDeadline.name,
        dueDate: companyResult.agmDeadline.dueDateFormatted,
        status: companyResult.agmDeadline.agmHeld ? 'compliant' : companyResult.agmDeadline.status,
        statusText: companyResult.agmDeadline.agmHeld ? 'Done' : statusLabel(companyResult.agmDeadline.status),
        markable: false,
      });
    }

    // All form deadlines. The dual-cycle view lists the same form twice (the
    // previous FY's overdue entry + the current FY's upcoming one), so the row
    // key must carry the cycle — the form code alone collides.
    for (const d of companyResult.deadlines) {
      rows.push({
        key: `${d.code}-${d.previousFyCycle ? 'prev' : 'current'}`,
        form: d.code,
        purpose: d.name,
        dueDate: d.pendingAgm ? 'After AGM' : d.dueDateFormatted,
        status: d.status,
        statusText: statusLabel(d.status),
        // Filed tracking. `cycleKey` is the FY half of the engine's filingKey.
        formKey: d.key,
        cycleKey: d.filingKey ? d.filingKey.split(':')[1] : '',
        cycleFyLabel: d.cycleFyLabel,
        previousFyCycle: !!d.previousFyCycle,
        filed: !!d.filed,
        // Provenance: how the filed state was established, and — for
        // registry-derived flags — the as-of date of the fetch behind it.
        filedSource: d.filedSource || null,
        filedBasis: d.filedBasis || '',
        filedOverridesMca: !!d.filedOverridesMca,
        // Optional evidence recorded with a manual flag (SRN / real filing date).
        filedSrn: d.filedSrn || '',
        filedOn: d.filedOn || null,
        filedHasEvidence: !!d.filedHasEvidence,
      });
    }

    return rows;
  }, [companyResult]);

  // Manual "Mark as Filed" — the engine is pure date arithmetic and can't know
  // a form was actually submitted, so without this a complied company keeps
  // showing last FY's forms as overdue with penalties it never owed.
  //
  // `filed: null` clears the manual note instead of asserting either way, which
  // is how a user backs out of an override and lets the registry-derived flag
  // (if any) speak again.
  // `evidence` — optional `{ srn, filedOn }`, only meaningful with `filed: true`.
  // Omitted entirely on the plain un-file / clear paths so the API's
  // absent-key-preserves rule is never triggered by an accidental undefined.
  const setFiled = useCallback(async (row, filed, evidence) => {
    const companyId = selectedCompany?._id || selectedCompany?.id;
    if (!companyId || !row.formKey || !row.cycleKey) return;
    setSavingRowKey(row.key);
    try {
      const result = await companiesAPI.setFilingStatus(companyId, {
        formKey: row.formKey,
        cycleKey: row.cycleKey,
        filed,
        ...(evidence || {}),
      });
      await fetchCompanies();
      setEvidenceFor(null);
      const what = filed === null ? 'note cleared' : filed ? 'marked as filed' : 'marked as not filed';
      // One toast, not two: the SRN was saved either way, so the format note is
      // a suffix on the success message rather than a second competing alert.
      const srnNote = result?.srnLooksValid === false ? ' — SRN saved as typed (unusual format)' : '';
      showToast(`${row.form} ${row.cycleFyLabel || ''} ${what}`.replace(/\s+/g, ' ') + srnNote, 'success');
    } catch (error) {
      showToast(error.message || 'Could not update filing status', 'error');
    } finally {
      setSavingRowKey(null);
    }
  }, [selectedCompany, fetchCompanies, showToast]);

  // ─── MCA refresh ───
  //
  // A registry refresh can only apply to a company we actually STORE — there is
  // no record to write the fetched fields onto otherwise. So the typed CIN box
  // resolves against the portfolio: a CIN that isn't there is an "add it" flow
  // (Add Company already fetches MCA data during create), not a refresh. Empty
  // box = refresh the company currently selected.
  const refreshTarget = useMemo(() => {
    const typed = cin.trim().toUpperCase();
    if (!typed) return selectedCompany;
    return companies.find((c) => String(c.cin || '').toUpperCase() === typed) || null;
  }, [cin, companies, selectedCompany]);

  // Same gating as Mark as Filed: the refresh endpoint requires read_write, so
  // don't offer a button the server would 403. Unknown level (older cached list)
  // errs on showing it; the server stays the enforcement point.
  const canRefreshMca = refreshTarget
    ? refreshTarget.myAccessLevel == null || ['read_write', 'admin'].includes(refreshTarget.myAccessLevel)
    : true; // nothing resolved yet — let the click explain why

  // What MCA data this record currently rests on. Shown verbatim so a stale
  // fetch is never read as current truth, and "never" is stated rather than
  // implied by an absent line.
  const mcaAsOfLabel = useMemo(() => {
    if (!selectedCompany) return '';
    const stamp = selectedCompany.mcaDataAsOf;
    return stamp ? `MCA data as of ${formatDateEN(stamp)}` : 'No MCA fetch on record';
  }, [selectedCompany]);

  // The real fetch. Server-side by design: the fetch and the `mcaDataAsOf`
  // provenance stamp are one transaction there, so the stamp can never end up on
  // hand-typed data. The response tells us exactly which fields changed, and we
  // report those rather than a vague "updated from MCA".
  const refreshFromMca = useCallback(async () => {
    const typed = cin.trim().toUpperCase();
    if (typed && !refreshTarget) {
      showToast(
        `${typed} isn't one of your companies yet — add it first, and its MCA data is fetched during Add.`,
        'info',
      );
      openAddCompanyModal?.();
      return;
    }
    if (!refreshTarget) {
      showToast('Select one of your companies to refresh from MCA', 'info');
      return;
    }
    const companyId = refreshTarget._id || refreshTarget.id;
    if (!companyId) return;

    setFetchingMca(true);
    try {
      const response = await companiesAPI.refreshFromMca(companyId);
      await fetchCompanies();
      // Point the checklist at whatever we just refreshed, so the user sees the
      // effect of the fetch instead of another company's rows.
      if (refreshTarget.cin) setSelectedCin(refreshTarget.cin);
      setCin('');

      const applied = response.applied || [];
      const when = response.mcaDataAsOf ? formatDateEN(response.mcaDataAsOf) : '';
      if (applied.length) {
        const names = applied.map((a) => a.label).join(', ');
        showToast(`Updated from MCA${when ? ` (as of ${when})` : ''}: ${names}`, 'success');
      } else {
        showToast(`Already matches MCA master data${when ? ` (checked ${when})` : ''}`, 'success');
      }
    } catch (error) {
      // Both failure modes leave the record untouched and unstamped, so say so
      // plainly rather than implying a partial update.
      showToast(error.message || 'Could not fetch MCA data — nothing was changed', 'error');
    } finally {
      setFetchingMca(false);
    }
  }, [cin, refreshTarget, fetchCompanies, showToast, openAddCompanyModal]);

  // AOC-4 vs AOC-4 XBRL. The determination is computed by the engine from
  // registry facts (listing status, paid-up capital) plus the three inputs the
  // registry cannot supply, and is shown with its basis and as-of date.
  const xbrl = companyResult?.xbrl || null;
  const hasAoc4Row = useMemo(
    () => !!companyResult?.deadlines?.some((d) => d.key === 'aoc4' || d.key === 'aoc4xbrl'),
    [companyResult],
  );

  const patchXbrl = useCallback(async (patch) => {
    const companyId = selectedCompany?._id || selectedCompany?.id;
    if (!companyId) return;
    setSavingXbrl(true);
    try {
      await companiesAPI.setXbrl(companyId, patch);
      await fetchCompanies();
      showToast('XBRL applicability updated', 'success');
    } catch (error) {
      showToast(error.message || 'Could not update XBRL applicability', 'error');
    } finally {
      setSavingXbrl(false);
    }
  }, [selectedCompany, fetchCompanies, showToast]);

  // Progress bars
  const progressBars = useMemo(() => {
    if (!companyResult) return [];
    const allItems = [...companyResult.deadlines, ...(companyResult.agmDeadline ? [companyResult.agmDeadline] : [])];
    return allItems
      .filter((d) => d.status !== 'pending-agm')
      .map((d) => {
        let value = 100, color = '#4a6b3f', text = d.status === 'filed' ? 'Filed' : 'On Track';
        if (d.status === 'overdue') { value = 20; color = '#9a564a'; text = 'Overdue'; }
        else if (d.status === 'due-soon') { value = 50; color = '#8a6a1f'; text = 'Due Soon'; }
        // The dual-cycle view lists the same form TWICE (previous FY's overdue
        // entry + this FY's upcoming one), so the form code alone is not a unique
        // React key — it collided, and React warned. The row keys below already
        // solved this by carrying the cycle; do the same here, and name the FY on
        // the previous-FY bar so two same-labelled bars are also readable.
        const key = `${d.code}-${d.previousFyCycle ? 'prev' : 'current'}`;
        const label = d.previousFyCycle && d.cycleFyLabel ? `${d.code} (${d.cycleFyLabel})` : d.code;
        return { key, label, value, color, text };
      })
      .slice(0, 4);
  }, [companyResult]);

  // Recording a filing goes through setFiled() above — a scoped PATCH of the
  // one form/cycle pair. It replaced an earlier flow that rewrote the whole
  // company record (addOrUpdateCompany with a rebuilt `filedForms` array), and
  // XBRL applicability likewise moved onto patchXbrl()'s `xbrlOverride`.

  // Penalty calculation
  const totalPenalty = companyResult?.totalPenalty || 0;
  const penaltyBreakdown = useMemo(() => {
    if (!companyResult) return '';
    const overdueItems = [...companyResult.deadlines, ...(companyResult.agmDeadline ? [companyResult.agmDeadline] : [])]
      .filter((d) => d.status === 'overdue');
    if (!overdueItems.length) return 'No penalties';
    return overdueItems.map((d) => `${d.code} (${Math.abs(d.daysRemaining)}d)`).join(' + ');
  }, [companyResult]);

  const entity = selectedCompany ? normalizeEntityType(selectedCompany.type) : 'pvtlimited';
  const entityLabel = ENTITY_LABELS[entity] || 'Private Limited';
  const hasCompanies = companies.length > 0;

  return (
    <AppLayout pageTitle="Annual Filing">
      <PageHeader
        title={`Annual Filing${companyResult ? ` \u2014 ${companyResult.fyLabel}` : ''}`}
        subtitle={selectedCompany
          ? `${selectedCompany.name} \u00B7 ${entityLabel} \u00B7 Annual compliance checklist`
          : 'Select a company to view its annual filing checklist'}
      />

      {/* MCA Search + Company Selector */}
      <div className="relative overflow-hidden rounded-[13px] p-5 mb-[18px]" style={{ background: 'linear-gradient(135deg,#0a3d3d,#08111f)', color: '#fff' }}>
        <div className="absolute right-[18px] top-1/2 -translate-y-1/2 opacity-[0.08]"><Icon name="search" size={44} /></div>
        <div className="text-[10px] tracking-[2px] uppercase font-bold text-[#b8973a] mb-[7px]">MCA Company Search</div>
        <div className="text-[16px] font-bold mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Fetch Company Data from MCA21 Portal</div>
        {/* Freshness stated up front — every registry-derived caption downstream
            (XBRL basis, filed-per-MCA rows) rests on this date. */}
        <div className="text-[11px] text-white/55 mb-3">
          {mcaAsOfLabel}
          {mcaAsOfLabel ? ' · ' : ''}
          Registry master data only — no filing history, turnover or AGM date is published.
        </div>
        <div className="flex gap-[7px] flex-wrap items-end">
          {hasCompanies && (
            <div className="flex flex-col gap-[5px]">
              <div className="text-[10px] uppercase tracking-[1.5px] text-white/60 font-bold">Your Companies</div>
              <div className="flex-1 min-w-[240px]">
                <Select
                  tone="dark"
                  value={selectedCin}
                  onChange={setSelectedCin}
                  placeholder="Select company..."
                  options={companies.map((c) => ({ value: c.cin, label: c.name || c.cin }))}
                />
              </div>
            </div>
          )}
          <div className="flex flex-col gap-[5px]">
            {/* Optional — the fetch applies to a company we STORE, so this box
                picks which one by CIN. Left blank it refreshes the selection. */}
            <div className="text-[10px] uppercase tracking-[1.5px] text-white/60 font-bold">CIN (optional)</div>
            <input
              value={cin}
              onChange={(event) => setCin(event.target.value.toUpperCase())}
              className="flex-1 min-w-[240px] border-[1.5px] rounded-[8px] px-[14px] py-[9px] text-[13px] bg-white/10 text-white placeholder:text-white/35 outline-none"
              style={{ borderColor: 'rgba(255,255,255,.15)' }}
              placeholder="Blank = the selected company"
            />
          </div>
          <Button
            variant="gold"
            className="self-end"
            leftIcon={<Icon name="search" size={15} />}
            onClick={refreshFromMca}
            loading={fetchingMca}
            disabled={!canRefreshMca}
            title={canRefreshMca
              ? 'Fetch MCA master data and apply the registry fields to this company'
              : `You have read-only access to ${refreshTarget?.name || 'this company'} — ask the owner for edit access to refresh it from MCA.`}
          >
            {fetchingMca ? 'Fetching…' : 'Fetch Data'}
          </Button>
        </div>
      </div>

      {/* Filing Checklist + Sidebar */}
      {companyResult ? (
        <div className="grid grid-cols-[2fr_1fr] gap-[18px] mb-[22px]">
          {/* Checklist Table */}
          <div className="bg-white rounded-[12px] border overflow-hidden" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
            <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: '#ddd6c6' }}>
              {/* JSX text is not a JS string literal \u2014 "\u2014" here rendered
                  the six characters verbatim. Interpolate the escape instead. */}
              <div className="flex items-center gap-2 text-[16px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                <Icon name="calendar" size={17} style={{ color: '#145f5f' }} /> {`Annual Filing Checklist \u2014 ${entityLabel}`}
              </div>
              {/* The XBRL override lives in the applicability panel below the
                  table, next to the determination and its basis — a bare
                  dropdown up here set the same thing with none of that
                  context. */}
            </div>
            <div className="p-[18px_20px] overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr style={{ background: '#f8f5ef', borderBottom: '1px solid #ddd6c6' }}>
                    <th className="text-left px-[14px] py-[10px] text-[10.5px] font-bold uppercase tracking-[1px] text-[#7a7060]">Form</th>
                    <th className="text-left px-[14px] py-[10px] text-[10.5px] font-bold uppercase tracking-[1px] text-[#7a7060]">Purpose</th>
                    <th className="text-left px-[14px] py-[10px] text-[10.5px] font-bold uppercase tracking-[1px] text-[#7a7060]">Due Date</th>
                    <th className="text-left px-[14px] py-[10px] text-[10.5px] font-bold uppercase tracking-[1px] text-[#7a7060]">Status</th>
                    <th className="text-left px-[14px] py-[10px] text-[10.5px] font-bold uppercase tracking-[1px] text-[#7a7060]">Filed</th>
                  </tr>
                </thead>
                <tbody>
                  {checklist.map((row) => (
                    <tr key={row.key || row.form} className="hover:bg-[#f8f5ef]" style={{ borderBottom: '1px solid #ddd6c6' }}>
                      <td className="px-[14px] py-[11px] text-[12.5px]">
                        <strong>{row.form}</strong>
                        {row.previousFyCycle && row.cycleFyLabel && (
                          <span className="ml-1.5 text-[10.5px] text-[#8a7d66]">{row.cycleFyLabel}</span>
                        )}
                      </td>
                      <td className="px-[14px] py-[11px] text-[12.5px]">{row.purpose}</td>
                      <td className="px-[14px] py-[11px] text-[12.5px]">{row.dueDate}</td>
                      <td className="px-[14px] py-[11px] text-[12.5px]">
                        {(() => { const c = STATUS_CHIP[row.status] || STATUS_CHIP.compliant; return (
                          <Chip bg={c.bg} color={c.color}>{row.statusText}</Chip>
                        ); })()}
                      </td>
                      <td className="px-[14px] py-[11px] text-[12.5px]">
                        {row.formKey && row.cycleKey && !canMarkFiled ? (
                          // Read-only collaborators get the state, not the control —
                          // the server 403s the write anyway.
                          row.filed ? (
                            <Chip bg="#e8efe5" color="#4a6b3f">Filed</Chip>
                          ) : (
                            <span className="text-[11px] text-[#a49b8b]" title="You have read-only access to this company">{'—'}</span>
                          )
                        ) : row.formKey && row.cycleKey ? (
                          <button
                            type="button"
                            disabled={savingRowKey === row.key}
                            // Marking filed opens the evidence dialog instead of
                            // flipping the flag instantly — the SRN is the only
                            // thing that makes the record checkable later, and
                            // asking for it AFTER the fact never happens.
                            // Un-filing stays a single click: there is no
                            // evidence for a filing that did not happen.
                            onClick={() => (row.filed
                              ? setFiled(row, false)
                              : setEvidenceFor({ row, mode: 'mark' }))}
                            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-[3px] text-[11px] font-semibold leading-[1.2] disabled:opacity-50"
                            style={row.filed
                              ? { borderColor: '#c6d6bd', background: '#e8efe5', color: '#4a6b3f' }
                              : { borderColor: '#ddd6c6', background: '#fff', color: '#7a7060' }}
                          >
                            <Icon name={row.filed ? 'success' : 'pending'} size={13} />
                            {row.filed ? 'Filed' : 'Mark filed'}
                          </button>
                        ) : (
                          <span className="text-[11px] text-[#a49b8b]">{'—'}</span>
                        )}
                        {/* Basis, never a bare assertion: a registry-derived flag
                            always carries where it came from and when it was
                            fetched, so a stale fetch can't read as current truth.
                            A manual flag shows its basis only when it HAS
                            evidence (SRN / filing date) — a bare "filed" stays
                            visually quiet rather than captioning every row with
                            "a user said so". */}
                        {(isRegistryFilingSource(row.filedSource) || row.filedHasEvidence) && row.filedBasis && (
                          <div className="mt-1 text-[10.5px] leading-[1.35] text-[#8a7d66]">{row.filedBasis}</div>
                        )}
                        {row.filed && !isRegistryFilingSource(row.filedSource) && canMarkFiled && (
                          <button
                            type="button"
                            disabled={savingRowKey === row.key}
                            onClick={() => setEvidenceFor({ row, mode: 'edit' })}
                            className="mt-1 block text-[10.5px] underline text-[#8a7d66] disabled:opacity-50"
                            title="Record or correct the SRN and the date this form was filed"
                          >
                            {row.filedHasEvidence ? 'Edit details' : 'Add SRN / date'}
                          </button>
                        )}
                        {row.filedOverridesMca && canMarkFiled && (
                          <button
                            type="button"
                            disabled={savingRowKey === row.key}
                            onClick={() => setFiled(row, null)}
                            className="mt-1 text-[10.5px] underline text-[#8a7d66] disabled:opacity-50"
                            title="Drop your manual note and use the MCA-derived value again"
                          >
                            Reset to MCA data
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Scope notes: what this checklist deliberately does not track. */}
              <div className="mt-[14px] rounded-[8px] p-[13px] text-[11.5px] leading-[1.55] text-[#7a7060]" style={{ background: '#f8f5ef' }}>
                {EVENT_BASED_FORMS.map((form) => (
                  <div key={form.code} className="flex gap-2">
                    <Icon name="info" size={14} style={{ color: '#8a7d66', flexShrink: 0, marginTop: 2 }} />
                    <span><strong>{form.code}</strong> ({form.section}) {form.note}</span>
                  </div>
                ))}
              </div>

              {/* AOC-4 XBRL applicability. Rule 3 of the XBRL Rules 2015 is an
                  OR, and two of its four limbs (listed, paid-up ≥ ₹5 cr) come
                  from MCA master data — so most companies resolve with nothing
                  asked. Only when both registry limbs are known-false does one
                  optional question remain. The basis and its as-of date are
                  always stated, because master data reflects the LAST FILED
                  position and a recent allotment leaves paid-up capital stale. */}
              {xbrl && hasAoc4Row && (
                <div
                  className="mt-[14px] rounded-[8px] border p-[13px] text-[11.5px] leading-[1.55]"
                  style={xbrl.required === true
                    ? { borderColor: '#e6d3a8', background: '#fbf7ec' }
                    : xbrl.required === false
                      ? { borderColor: '#d6e3cf', background: '#f4f8f1' }
                      : { borderColor: '#ddd6c6', background: '#f8f5ef' }}
                >
                  <div className="flex gap-2">
                    <Icon
                      name={xbrl.required === true ? 'warning' : xbrl.required === false ? 'success' : 'info'}
                      size={14}
                      style={{ color: xbrl.required === true ? '#8a6a1f' : xbrl.required === false ? '#4a6b3f' : '#8a7d66', flexShrink: 0, marginTop: 2 }}
                    />
                    <div className="flex-1">
                      <div className="font-bold text-[12px] text-[#3f3a31]">
                        {xbrl.required === true
                          ? 'AOC-4 XBRL required'
                          : xbrl.required === false
                            ? 'Plain AOC-4'
                            : 'XBRL applicability not determined'}
                      </div>
                      <div className="mt-[3px] text-[#7a7060]">{xbrl.reason}</div>

                      {canMarkFiled && (
                        <div className="mt-[9px] flex flex-col gap-[7px]">
                          {/* The single remaining question. Turnover is not in
                              MCA master data — it lives in the financial
                              statements — so it is asked, once.
                              It STAYS visible once answered, with the answer
                              shown and "Not sure" as a way back. Hiding it on
                              answer was an un-answer trap: a mis-tapped yes/no
                              became a permanent statutory claim, and "Auto (from
                              data)" below only clears the override, not this.
                              Not rendered when the answer was inferred from a
                              stored turnover FIGURE — there would be nothing for
                              "Not sure" to clear, and offering it would lie. */}
                          {(xbrl.askTurnover || xbrl.turnoverAnswered) && (
                            <div className="flex items-center gap-[7px] flex-wrap">
                              <span className="text-[#7a7060]">Turnover ₹100 crore or more?</span>
                              {[
                                ['yes', 'Yes', 'Turnover is ₹100 crore or more — AOC-4 XBRL applies'],
                                ['no', 'No', 'Turnover is under ₹100 crore'],
                                ['', 'Not sure', 'Clear the answer — the determination goes back to "not determined"'],
                              ].map(([value, label, hint]) => {
                                const active = String(selectedCompany?.turnoverAbove100Cr || '') === value;
                                return (
                                  <button
                                    key={value || 'unknown'}
                                    type="button"
                                    disabled={savingXbrl}
                                    title={hint}
                                    onClick={() => patchXbrl({ turnoverAbove100Cr: value })}
                                    className="rounded-full border px-2.5 py-[2px] text-[11px] font-semibold disabled:opacity-50"
                                    style={active
                                      ? { borderColor: '#145f5f', background: '#e7f0f0', color: '#145f5f' }
                                      : { borderColor: '#ddd6c6', background: '#fff', color: '#7a7060' }}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          <div className="flex items-center gap-[7px] flex-wrap">
                            <span className="text-[#7a7060]">Set manually:</span>
                            {[
                              ['required', 'XBRL required'],
                              ['not_required', 'Not applicable'],
                              ['', 'Auto (from data)'],
                            ].map(([value, label]) => {
                              const active = String(selectedCompany?.xbrlOverride || '') === value;
                              return (
                                <button
                                  key={value || 'auto'}
                                  type="button"
                                  disabled={savingXbrl}
                                  onClick={() => patchXbrl({ xbrlOverride: value })}
                                  className="rounded-full border px-2.5 py-[2px] text-[11px] font-semibold disabled:opacity-50"
                                  style={active
                                    ? { borderColor: '#145f5f', background: '#e7f0f0', color: '#145f5f' }
                                    : { borderColor: '#ddd6c6', background: '#fff', color: '#7a7060' }}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>

                          {/* Sticky rule: once a company has filed in XBRL it
                              must keep filing in XBRL even after it drops below
                              every threshold.
                              The stickiness is a statement about COMPANIES, not
                              about this checkbox. Making the control itself
                              one-way meant a single misclick permanently forced
                              XBRL on a company that had never filed in it, with
                              no correction anywhere in the UI. So un-ticking is
                              allowed — but only through a confirm that spells out
                              that it corrects a mistaken entry and is not an
                              escape from the rule. */}
                          <div>
                            <label className="inline-flex items-start gap-[7px] text-[#7a7060] cursor-pointer">
                              <input
                                type="checkbox"
                                className="mt-[2px]"
                                disabled={savingXbrl}
                                checked={selectedCompany?.xbrlFiledPreviously === true}
                                onChange={(event) => {
                                  if (event.target.checked) patchXbrl({ xbrlFiledPreviously: true });
                                  else setConfirmStickyOff(true);
                                }}
                              />
                              <span>Has filed in XBRL before — must continue in XBRL</span>
                            </label>
                            {selectedCompany?.xbrlFiledPreviously === true && (
                              <div className="mt-[3px] ml-[22px] text-[10.5px] leading-[1.4] text-[#8a7d66]">
                                A company that has genuinely filed in XBRL cannot return to plain
                                AOC-4. Un-tick only to correct a mistaken entry.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar: Progress + Penalty */}
          <div className="flex flex-col gap-[14px]">
            {/* Filing Progress */}
            <div className="bg-white rounded-[12px] border overflow-hidden" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
              <div className="px-5 py-4 border-b" style={{ borderColor: '#ddd6c6' }}>
                <div className="flex items-center gap-2 text-[16px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}><Icon name="reporting" size={17} style={{ color: '#145f5f' }} /> Filing Progress</div>
              </div>
              <div className="p-[18px_20px]">
                {progressBars.map(({ key, label, value, color, text }) => (
                  <div key={key} className="mb-[10px] last:mb-0">
                    <div className="flex justify-between text-[11.5px] mb-1">
                      <span>{label}</span>
                      <span style={{ color, fontWeight: 700 }}>{text}</span>
                    </div>
                    <div className="h-[5px] rounded-full overflow-hidden" style={{ background: '#f8f5ef' }}>
                      <div className="h-full rounded-full" style={{ width: `${value}%`, background: color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Penalty Estimator */}
            <div className="bg-white rounded-[12px] border overflow-hidden" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,.07)' }}>
              <div className="px-5 py-4 border-b" style={{ borderColor: '#ddd6c6' }}>
                <div className="flex items-center gap-2 text-[16px] font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}><Icon name="penalty" size={17} style={{ color: '#8a6a1f' }} /> Penalty Estimator</div>
              </div>
              <div className="p-[18px_20px]">
                <div className="rounded-[8px] p-[13px] mb-[9px]" style={{ background: '#f8f5ef' }}>
                  <div className="text-[11px] text-[#7a7060] mb-1">Current Estimated Penalty</div>
                  <div className="text-[28px] font-bold" style={{ color: totalPenalty > 0 ? '#9a564a' : '#4a6b3f', fontFamily: "'Cormorant Garamond', serif" }}>
                    {totalPenalty > 0 ? `\u20B9${totalPenalty.toLocaleString('en-IN')}` : '\u20B90'}
                  </div>
                  <div className="text-[11px] text-[#7a7060] mt-[3px]">{penaltyBreakdown}</div>
                </div>
                {totalPenalty > 0 && (
                  <Button
                    variant="danger"
                    size="sm"
                    block
                    leftIcon={<Icon name="warning" size={14} />}
                    onClick={() => showToast('Redirecting to filing workflow', 'warning')}
                  >
                    File Now to Stop Penalty
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : hasCompanies ? (
        <EmptyState
          variant="inline"
          icon={(props) => <Icon name="calendar" {...props} />}
          title="Pick a company to see its checklist"
          subtitle="Select a company from the dropdown above to view its annual filing forms, due dates and penalty exposure."
        />
      ) : (
        <EmptyState
          variant="hero"
          illustration={<IlloPortfolio />}
          title="No companies to file for yet"
          subtitle="Add a company to your portfolio and we'll build its annual filing checklist — every form, due date and penalty tracked automatically."
          action={<Button variant="gold" size="sm" className="!h-[38px] !rounded-xl" onClick={openAddCompanyModal}>Add a company</Button>}
        />
      )}

      {/* Keyed on the row so re-opening for a different form starts from that
          row's stored evidence rather than the previous row's edits. */}
      {evidenceFor && (
        <FilingEvidenceDialog
          key={`${evidenceFor.row.key}-${evidenceFor.mode}`}
          row={evidenceFor.row}
          mode={evidenceFor.mode}
          companyName={selectedCompany?.name || ''}
          saving={savingRowKey === evidenceFor.row.key}
          onCancel={() => setEvidenceFor(null)}
          onSave={(evidence) => setFiled(evidenceFor.row, true, evidence)}
        />
      )}

      <ConfirmDialog
        open={confirmStickyOff}
        variant="warning"
        title="Correct a mistaken entry?"
        message="Un-ticking this does not exempt the company from XBRL. Once a company has genuinely filed its financial statements in XBRL, it must keep filing in XBRL even if it later falls below every threshold."
        note="Only un-tick if this box was ticked by mistake and the company has never actually filed in XBRL."
        confirmLabel="It was a mistake"
        cancelLabel="Keep it ticked"
        loading={savingXbrl}
        onClose={() => setConfirmStickyOff(false)}
        onConfirm={async () => {
          await patchXbrl({ xbrlFiledPreviously: false });
          setConfirmStickyOff(false);
        }}
      />
    </AppLayout>
  );
}
